/**
 * Wrapper em torno do @microsoft/fetch-event-source.
 *
 * O endpoint do backend retorna text/event-stream em resposta a um POST
 * com body JSON, o que NAO eh suportado pelo EventSource nativo (so
 * GET, sem header Authorization). fetch-event-source resolve ambos.
 */

import {
  fetchEventSource,
  type EventSourceMessage,
} from "@microsoft/fetch-event-source";

import { env } from "@/lib/env";

export interface SseHandler {
  /** Token a ser enviado em Authorization: Bearer <token>. */
  token?: string | null;
  /** Chamado para cada evento. */
  onEvent: (event: { event: string; data: unknown }) => void;
  /** Chamado quando o servidor encerra (event: done OU stream fim). */
  onClose?: () => void;
  /** Erro de rede ou parsing. */
  onError?: (err: unknown) => void;
}

export interface SseRequest {
  path: string;
  method?: "POST" | "GET";
  body?: unknown;
}

/**
 * Abre um SSE stream POST. Retorna um ``AbortController`` para o caller
 * fechar conexao sob demanda (clicar em "Parar").
 *
 * Implementacao:
 * - Headers: Authorization Bearer (se fornecido), Content-Type JSON,
 *   Accept text/event-stream.
 * - Body: JSON.stringify do payload.
 * - Por padrao fetch-event-source faz re-conexao automatica em erros
 *   de rede; desabilitamos via openWhenHidden=false + nao throw em
 *   onError (default e re-conectar). Para chat queremos comportamento
 *   "abre uma vez; ao terminar/cancelar nao retoma sozinho".
 */
export function openSseStream(
  req: SseRequest,
  handler: SseHandler,
): AbortController {
  const controller = new AbortController();
  const url = `${env.API_BASE_URL}${req.path}`;
  const headers: Record<string, string> = {
    "Content-Type": "application/json",
    Accept: "text/event-stream",
  };
  if (handler.token) headers.Authorization = `Bearer ${handler.token}`;

  void fetchEventSource(url, {
    method: req.method ?? "POST",
    headers,
    body: req.body !== undefined ? JSON.stringify(req.body) : undefined,
    signal: controller.signal,
    openWhenHidden: true,
    async onopen(response) {
      if (response.ok) return;
      throw new Error(`SSE error ${response.status}`);
    },
    onmessage(msg: EventSourceMessage) {
      if (!msg.event && !msg.data) return;
      let parsed: unknown = msg.data;
      try {
        parsed = JSON.parse(msg.data);
      } catch {
        // Mantem string crua se parse falha.
      }
      handler.onEvent({ event: msg.event || "message", data: parsed });
      if (msg.event === "done") {
        controller.abort();
      }
    },
    onclose() {
      handler.onClose?.();
    },
    onerror(err) {
      handler.onError?.(err);
      // Throw aborta a retentativa.
      throw err;
    },
  });

  return controller;
}
